import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Crown, Zap, ArrowRight, Shield, AlertTriangle, X } from 'lucide-react';
import { usePlanRestrictions, PLAN_NAMES, FEATURE_REQUIREMENTS } from '../hooks/usePlanRestrictions';

/**
 * PlanRestrictedPage - Popup overlay for plan-restricted features
 * Shows the actual page content behind a 50% dark overlay with a centered popup
 */
const PlanRestrictedPage = ({
  feature,
  title,
  description,
  icon: Icon = Shield,
  children
}) => {
  const navigate = useNavigate();
  const { hasFeature, currentPlan, isFreePlan, loading } = usePlanRestrictions();

  // Show loading state briefly
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-black flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-brand-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if user has access to this feature
  const hasAccess = hasFeature(feature);

  // If user has access, render children normally
  if (hasAccess) {
    return <>{children}</>;
  }

  // Get required plan for this feature
  const requiredPlan = FEATURE_REQUIREMENTS[feature] || 'pro_audit';
  const requiredPlanName = PLAN_NAMES[requiredPlan] || 'Plan Pro';

  return (
    <div className="relative">
      {/* Contenu reel de la page visible en arriere-plan */}
      <div className="pointer-events-none select-none">
        {children}
      </div>

      {/* Overlay sombre a 50% */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-50" />

      {/* Popup centree */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-brand-black-light border-2 border-red-500/50 rounded-2xl p-8 max-w-lg w-full text-center shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in duration-300">
          {/* Bouton fermer */}
          <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 p-2 text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
            <Lock className="w-10 h-10 text-red-500" />
          </div>

          <h2 className="text-2xl font-bold text-white mb-3 flex items-center justify-center gap-2">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            Fonctionnalité Premium
          </h2>

          <p className="text-gray-400 mb-6">
            Cette fonctionnalité n'est pas disponible avec le <span className="text-red-400 font-semibold">{PLAN_NAMES[currentPlan] || 'Plan Gratuit'}</span>.
            <br />
            Passez au <span className="text-brand-green font-semibold">{requiredPlanName}</span> ou supérieur pour débloquer cette page.
          </p>

          {/* Features incluses */}
          <div className="bg-brand-black rounded-xl p-4 mb-6 text-left">
            <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-yellow-500" />
              Inclus avec {requiredPlanName} :
            </h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-green" />
                Accès complet à cette fonctionnalité
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-green" />
                Outils professionnels activés
              </li>
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-green" />
                Support prioritaire
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl font-medium transition-colors"
            >
              Retour
            </button>
            <button
              onClick={() => window.location.href = 'https://sylink-pentest.com/pricing'}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-brand-green to-green-600 hover:from-green-600 hover:to-brand-green text-black rounded-xl font-bold transition-all flex items-center justify-center gap-2"
            >
              <Crown className="w-5 h-5" />
              Voir les offres
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanRestrictedPage;
